import React, { useEffect, useRef, useState, useCallback } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Divider,
    Grid,
    IconButton,
    LinearProgress,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { useForm } from "react-hook-form";
import { useSnackbar } from "notistack";

import UploadIcon from "@mui/icons-material/Upload";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import PageTitle from "../../components/PageTitle";
import DownloadSampleFile from "../../components/DownloadSampleFile";
import { FormProvider, RHFFileField } from "../../common/hook-form";
import {
    apiConstants,
    postMultipart,
    postGeneric,
    getGeneric,
} from "../../common/constants";
import { parseDateAndTime } from "../../helpers/UtilityHelper";

const SAMPLE_FILE_NAME = "DWS_WEIGHT_UPLOAD";
const ACTIVE_BATCH_KEY = "dwsWeightUpload.activeBatchId";
const POLL_INTERVAL_MS = 5000;
const STUCK_WARNING_MS = 15 * 60 * 1000;

const TERMINAL_STATUSES = ["COMPLETED", "COMPLETED_WITH_ERRORS", "FAILED"];

const STATUS_META = {
    INITIATED: { color: "default", label: "Initiated" },
    PROCESSING: { color: "info", label: "Processing" },
    COMPLETED: { color: "success", label: "Completed" },
    COMPLETED_WITH_ERRORS: { color: "warning", label: "Completed with errors" },
    FAILED: { color: "error", label: "Failed" },
};

// ─── KPI Card ──────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, color = "#1565c0", bg = "#e3f2fd" }) {
    return (
        <Box
            sx={{
                borderRadius: 2.5,
                border: "1px solid #e8e8e8",
                backgroundColor: "#fff",
                p: 2,
                height: "100%",
                display: "flex",
                gap: 1.5,
                alignItems: "flex-start",
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    backgroundColor: bg,
                    border: `1px solid ${color}25`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    color,
                }}
            >
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        color: "#9e9e9e",
                        fontWeight: 600,
                        fontSize: "0.68rem",
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                    }}
                >
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: "1.6rem",
                        color: "#1a1a1a",
                        lineHeight: 1.1,
                        mt: 0.4,
                    }}
                >
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Metadata field ────────────────────────────────────────────────────────────
function MetaField({ label, children }) {
    return (
        <Box>
            <Typography
                sx={{
                    color: "#9e9e9e",
                    fontWeight: 600,
                    fontSize: "0.68rem",
                    textTransform: "uppercase",
                    letterSpacing: 0.5,
                    mb: 0.5,
                }}
            >
                {label}
            </Typography>
            <Box sx={{ fontSize: "0.875rem", color: "#1a1a1a" }}>{children}</Box>
        </Box>
    );
}

// ─── Empty state ───────────────────────────────────────────────────────────────
function NoActiveBatch() {
    return (
        <Card sx={{ boxShadow: "none", border: "1px solid #e8e8e8" }}>
            <CardContent sx={{ textAlign: "center", py: 6, px: 3 }}>
                <Box
                    sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 1.5,
                    }}
                >
                    <InsertDriveFileOutlinedIcon
                        sx={{ fontSize: 30, color: "#bdbdbd" }}
                    />
                </Box>
                <Typography
                    sx={{
                        fontWeight: 700,
                        fontSize: "1rem",
                        color: "#424242",
                        mb: 0.5,
                    }}
                >
                    No active batch
                </Typography>
                <Typography
                    sx={{
                        color: "#9e9e9e",
                        fontSize: "0.82rem",
                        maxWidth: 420,
                        mx: "auto",
                        lineHeight: 1.6,
                    }}
                >
                    Upload a CSV above to start a DWS weight batch. Progress will
                    appear here and refresh automatically every few seconds.
                </Typography>
            </CardContent>
        </Card>
    );
}

function DwsWeightUpload() {
    const { enqueueSnackbar } = useSnackbar();
    const methods = useForm();
    const { handleSubmit, reset, watch } = methods;
    const selectedFile = watch("file")?.[0];

    const [isUploading, setIsUploading] = useState(false);
    const [isReplaying, setIsReplaying] = useState(false);
    const [isFetchingStatus, setIsFetchingStatus] = useState(false);
    const [batchId, setBatchId] = useState(() => {
        try {
            return localStorage.getItem(ACTIVE_BATCH_KEY) || "";
        } catch {
            return "";
        }
    });
    const [statusData, setStatusData] = useState(null);
    const [pollStartedAt, setPollStartedAt] = useState(null);

    const pollTimerRef = useRef(null);

    const persistBatchId = (id) => {
        try {
            if (id) localStorage.setItem(ACTIVE_BATCH_KEY, id);
            else localStorage.removeItem(ACTIVE_BATCH_KEY);
        } catch {
            // localStorage may be unavailable
        }
    };

    const stopPolling = () => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    const fetchStatus = useCallback(
        async (idOverride) => {
            const id = idOverride || batchId;
            if (!id) return null;
            setIsFetchingStatus(true);
            try {
                const res = await getGeneric(
                    `${apiConstants.DWS_WEIGHT_STATUS}/${id}`
                );
                const data = res?.data?.responseObj || res?.data;
                setStatusData(data);
                return data;
            } catch (err) {
                console.error("DWS status fetch failed", err);
                if (err?.response?.status === 404) {
                    enqueueSnackbar("Batch not found. Cleared active batch.", {
                        variant: "warning",
                    });
                    setBatchId("");
                    setStatusData(null);
                    persistBatchId("");
                    stopPolling();
                } else {
                    enqueueSnackbar(
                        err?.response?.data?.error ||
                            err?.response?.data?.message ||
                            "Failed to fetch upload status",
                        { variant: "error" }
                    );
                }
                return null;
            } finally {
                setIsFetchingStatus(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [batchId]
    );

    useEffect(() => {
        if (!batchId) {
            stopPolling();
            return undefined;
        }
        fetchStatus(batchId);
        if (!pollStartedAt) setPollStartedAt(Date.now());
        return () => {
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId]);

    useEffect(() => {
        const status = statusData?.status;
        if (!batchId || !status) {
            stopPolling();
            return undefined;
        }
        if (TERMINAL_STATUSES.includes(status)) {
            stopPolling();
            return undefined;
        }
        if (!pollTimerRef.current) {
            pollTimerRef.current = setInterval(() => {
                fetchStatus(batchId);
            }, POLL_INTERVAL_MS);
        }
        return () => {
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [batchId, statusData?.status]);

    const onSubmit = async (data) => {
        if (!data?.file?.[0]) {
            enqueueSnackbar("Please select a CSV file", { variant: "error" });
            return;
        }
        setIsUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", data.file[0]);
            const res = await postMultipart(
                apiConstants.DWS_WEIGHT_UPLOAD,
                formData
            );
            const payload = res?.data || {};
            const newBatchId = payload.batchId;
            if (!newBatchId) {
                enqueueSnackbar(
                    payload?.error || payload?.message || "Upload failed",
                    { variant: "error" }
                );
                return;
            }
            const variant =
                payload.skippedRows && payload.skippedRows > 0
                    ? "warning"
                    : "success";
            enqueueSnackbar(payload.message || "File uploaded successfully", {
                variant,
            });
            setBatchId(newBatchId);
            persistBatchId(newBatchId);
            setStatusData(null);
            setPollStartedAt(Date.now());
            reset();
        } catch (err) {
            console.error("DWS upload failed", err);
            const apiError =
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                err?.message ||
                "Upload failed";
            enqueueSnackbar(apiError, { variant: "error" });
        } finally {
            setIsUploading(false);
        }
    };

    const handleManualRefresh = () => {
        if (!batchId) return;
        fetchStatus(batchId);
    };

    const handleReplay = async () => {
        if (!batchId) return;
        setIsReplaying(true);
        try {
            const res = await postGeneric(
                `${apiConstants.DWS_WEIGHT_REPLAY}/${batchId}`
            );
            enqueueSnackbar(
                res?.data?.message || "Replay triggered for this batch",
                { variant: "success" }
            );
            setPollStartedAt(Date.now());
            await fetchStatus(batchId);
        } catch (err) {
            console.error("DWS replay failed", err);
            enqueueSnackbar(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    "Failed to trigger replay",
                { variant: "error" }
            );
        } finally {
            setIsReplaying(false);
        }
    };

    const handleClearBatch = () => {
        stopPolling();
        setBatchId("");
        setStatusData(null);
        setPollStartedAt(null);
        persistBatchId("");
    };

    const handleCopyBatchId = () => {
        if (!batchId) return;
        try {
            navigator.clipboard.writeText(batchId);
            enqueueSnackbar("Batch ID copied", { variant: "info" });
        } catch {
            enqueueSnackbar("Unable to copy", { variant: "error" });
        }
    };

    const status = statusData?.status;
    const statusMeta = STATUS_META[status] || { color: "default", label: status || "—" };
    const isTerminal = TERMINAL_STATUSES.includes(status);
    const totalRows = statusData?.totalRows || 0;
    const processedRows = statusData?.processedRows || 0;
    const skippedRows = statusData?.skippedRows || 0;
    const failedRows = statusData?.failedRows || 0;
    const progressPct =
        totalRows > 0
            ? Math.min(100, Math.round((processedRows / totalRows) * 100))
            : 0;

    const isStuck =
        !!batchId &&
        status &&
        !isTerminal &&
        pollStartedAt &&
        Date.now() - pollStartedAt > STUCK_WARNING_MS;

    const canReplay =
        !!batchId &&
        (status === "FAILED" || status === "COMPLETED_WITH_ERRORS" || isStuck);

    return (
        <>
            <PageTitle>DWS Weight Upload</PageTitle>

            <Grid container spacing={2}>
                {/* ─── Upload Card ──────────────────────────────────────── */}
                <Grid item xs={12}>
                    <Card sx={{ boxShadow: "none", border: "1px solid #e8e8e8" }}>
                        <CardContent>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                                sx={{ mb: 2 }}
                            >
                                <Box>
                                    <Typography sx={{ fontWeight: 700, fontSize: "1rem", color: "#1a1a1a" }}>
                                        Upload DWS Weight CSV
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Uploads are processed asynchronously. Only one upload can be in progress at a time.
                                    </Typography>
                                </Box>
                            </Stack>

                            {/* Sample template card */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                    p: 2,
                                    border: "1px dashed #ccc",
                                    borderRadius: 2,
                                    mb: 2.5,
                                    backgroundColor: "#fafafa",
                                }}
                            >
                                <DescriptionIcon color="action" />
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2">
                                        Sample CSV Template
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        Download and fill in the required fields before uploading.
                                    </Typography>
                                </Box>
                                <DownloadSampleFile
                                    fileName={SAMPLE_FILE_NAME}
                                    label="Download Sample"
                                    variant="outlined"
                                    size="small"
                                />
                            </Box>

                            <Divider sx={{ mb: 2.5 }} />

                            {/* Dropzone */}
                            <FormProvider
                                methods={methods}
                                onSubmit={handleSubmit(onSubmit)}
                            >
                                <Box
                                    sx={{
                                        p: 3,
                                        border: "2px dashed #e0e0e0",
                                        borderRadius: 2,
                                        textAlign: "center",
                                        backgroundColor: "#fafafa",
                                        mb: 2,
                                    }}
                                >
                                    <CloudUploadIcon
                                        sx={{
                                            fontSize: 48,
                                            color: "text.secondary",
                                            mb: 1,
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        sx={{ mb: 0.5 }}
                                    >
                                        Choose your DWS weight CSV
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        display="block"
                                        sx={{ mb: 2 }}
                                    >
                                        Supported format: CSV (.csv)
                                    </Typography>
                                    <RHFFileField
                                        name="file"
                                        required
                                        fileType={[".csv"]}
                                        label={
                                            selectedFile
                                                ? "Click to choose a different file"
                                                : "Click to select CSV file"
                                        }
                                    />
                                </Box>

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={1.5}
                                >
                                    <LoadingButton
                                        loading={isUploading}
                                        variant="contained"
                                        size="medium"
                                        startIcon={<UploadIcon />}
                                        type="submit"
                                        sx={{ minWidth: 160 }}
                                    >
                                        Upload
                                    </LoadingButton>
                                </Stack>
                            </FormProvider>
                        </CardContent>
                    </Card>
                </Grid>

                {/* ─── Active Batch ─────────────────────────────────────── */}
                <Grid item xs={12}>
                    {batchId ? (
                        <Card sx={{ boxShadow: "none", border: "1px solid #e8e8e8" }}>
                            <CardContent>
                                {/* Header */}
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: "flex-start", md: "center" }}
                                    spacing={1.5}
                                    sx={{ mb: 2.5 }}
                                >
                                    <Box>
                                        <Stack
                                            direction="row"
                                            spacing={1.5}
                                            alignItems="center"
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: "1rem",
                                                    color: "#1a1a1a",
                                                }}
                                            >
                                                Active Batch
                                            </Typography>
                                            <Chip
                                                label={statusMeta.label}
                                                color={statusMeta.color}
                                                size="small"
                                                variant={
                                                    isTerminal
                                                        ? "filled"
                                                        : "outlined"
                                                }
                                                sx={{ fontWeight: 600 }}
                                            />
                                        </Stack>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {isTerminal
                                                ? "Final status. Upload another file to start a new batch."
                                                : "Auto-refreshes every 5 seconds while processing."}
                                        </Typography>
                                    </Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        flexWrap="wrap"
                                    >
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<RefreshIcon />}
                                            onClick={handleManualRefresh}
                                            disabled={isFetchingStatus}
                                        >
                                            Refresh
                                        </Button>
                                        {canReplay && (
                                            <LoadingButton
                                                size="small"
                                                variant="contained"
                                                color="warning"
                                                startIcon={<ReplayIcon />}
                                                onClick={handleReplay}
                                                loading={isReplaying}
                                            >
                                                Replay
                                            </LoadingButton>
                                        )}
                                        {isTerminal && (
                                            <Button
                                                size="small"
                                                variant="text"
                                                color="error"
                                                onClick={handleClearBatch}
                                            >
                                                Clear
                                            </Button>
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Stuck warning */}
                                {isStuck && (
                                    <Alert
                                        severity="warning"
                                        icon={<WarningAmberIcon />}
                                        sx={{ mb: 2.5 }}
                                    >
                                        <Typography variant="body2">
                                            Processing is taking longer than expected. Please contact support with batch ID{" "}
                                            <strong>{batchId}</strong> or click Replay to retry.
                                        </Typography>
                                    </Alert>
                                )}

                                {/* KPI Cards */}
                                <Grid container spacing={2} sx={{ mb: 2.5 }}>
                                    <Grid item xs={6} md={3}>
                                        <KpiCard
                                            icon={
                                                <InsertDriveFileOutlinedIcon />
                                            }
                                            label="Total Rows"
                                            value={totalRows}
                                            color="#1565c0"
                                            bg="#e3f2fd"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <KpiCard
                                            icon={<TaskAltIcon />}
                                            label="Processed"
                                            value={processedRows}
                                            color="#2e7d32"
                                            bg="#e8f5e9"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <KpiCard
                                            icon={
                                                <RemoveCircleOutlineIcon />
                                            }
                                            label="Skipped"
                                            value={skippedRows}
                                            color="#ed6c02"
                                            bg="#fff4e5"
                                        />
                                    </Grid>
                                    <Grid item xs={6} md={3}>
                                        <KpiCard
                                            icon={<ErrorOutlineIcon />}
                                            label="Failed"
                                            value={failedRows}
                                            color={
                                                failedRows > 0
                                                    ? "#c62828"
                                                    : "#9e9e9e"
                                            }
                                            bg={
                                                failedRows > 0
                                                    ? "#ffebee"
                                                    : "#f5f5f5"
                                            }
                                        />
                                    </Grid>
                                </Grid>

                                {/* Progress bar */}
                                <Box
                                    sx={{
                                        p: 2,
                                        backgroundColor: "#fafafa",
                                        border: "1px solid #f0f0f0",
                                        borderRadius: 2,
                                        mb: 2.5,
                                    }}
                                >
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        sx={{ mb: 1 }}
                                    >
                                        <Stack
                                            direction="row"
                                            spacing={1}
                                            alignItems="center"
                                        >
                                            {!isTerminal && (
                                                <HourglassEmptyIcon
                                                    sx={{
                                                        fontSize: 16,
                                                        color: "#9e9e9e",
                                                    }}
                                                />
                                            )}
                                            <Typography
                                                sx={{
                                                    fontWeight: 600,
                                                    fontSize: "0.78rem",
                                                    color: "#424242",
                                                    textTransform: "uppercase",
                                                    letterSpacing: 0.4,
                                                }}
                                            >
                                                Progress
                                            </Typography>
                                        </Stack>
                                        <Typography
                                            sx={{
                                                fontWeight: 700,
                                                fontSize: "0.82rem",
                                                color: "#424242",
                                            }}
                                        >
                                            {processedRows.toLocaleString()} /{" "}
                                            {totalRows.toLocaleString()}{" "}
                                            <Box
                                                component="span"
                                                sx={{
                                                    color: "#9e9e9e",
                                                    fontWeight: 500,
                                                }}
                                            >
                                                ({progressPct}%)
                                            </Box>
                                        </Typography>
                                    </Stack>
                                    <LinearProgress
                                        variant="determinate"
                                        value={progressPct}
                                        sx={{
                                            height: 10,
                                            borderRadius: 5,
                                            backgroundColor: "#eef2f7",
                                            "& .MuiLinearProgress-bar": {
                                                borderRadius: 5,
                                            },
                                        }}
                                    />
                                </Box>

                                {/* Metadata grid */}
                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <MetaField label="Batch ID">
                                            <Stack
                                                direction="row"
                                                spacing={0.5}
                                                alignItems="center"
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        fontFamily: "monospace",
                                                        wordBreak: "break-all",
                                                    }}
                                                >
                                                    {batchId}
                                                </Box>
                                                <Tooltip title="Copy Batch ID">
                                                    <IconButton
                                                        size="small"
                                                        onClick={handleCopyBatchId}
                                                        sx={{ p: 0.5 }}
                                                    >
                                                        <ContentCopyIcon
                                                            sx={{
                                                                fontSize: 14,
                                                            }}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </MetaField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MetaField label="Uploaded By">
                                            {statusData?.uploadedBy || "—"}
                                        </MetaField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MetaField label="Uploaded At">
                                            {statusData?.createdAt
                                                ? parseDateAndTime(
                                                      statusData.createdAt
                                                  )
                                                : "—"}
                                        </MetaField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <MetaField label="Completed At">
                                            {statusData?.completedAt
                                                ? parseDateAndTime(
                                                      statusData.completedAt
                                                  )
                                                : "—"}
                                        </MetaField>
                                    </Grid>
                                </Grid>

                                {/* Error report */}
                                {statusData?.errorReportUrl && (
                                    <>
                                        <Divider sx={{ my: 2.5 }} />
                                        <Stack
                                            direction={{ xs: "column", sm: "row" }}
                                            spacing={1.5}
                                            alignItems={{ xs: "flex-start", sm: "center" }}
                                            justifyContent="space-between"
                                        >
                                            <Box>
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: "0.85rem",
                                                        color: "#1a1a1a",
                                                    }}
                                                >
                                                    Error report available
                                                </Typography>
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                >
                                                    Download a CSV of failed rows with reasons. Pre-signed link, valid for 24 hours.
                                                </Typography>
                                            </Box>
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                startIcon={<OpenInNewIcon />}
                                                href={statusData.errorReportUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Download Error Report
                                            </Button>
                                        </Stack>
                                    </>
                                )}
                            </CardContent>
                        </Card>
                    ) : (
                        <NoActiveBatch />
                    )}
                </Grid>
            </Grid>
        </>
    );
}

export default DwsWeightUpload;
